import React, { useState, createRef, memo, useRef } from "react";
import Dropzone, { DropzoneRef } from "react-dropzone";

import {
  Box,
  Stack,
  Typography,
  ButtonGroup,
  Button,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { InsertDriveFile } from "@mui/icons-material";
import { defaultLanguage, languages } from "../../refs";
import ReactHlsPlayer from "react-hls-player";
import { UploadFile } from "@mui/icons-material";
import { toastMessage } from "src/helpers/mixins";

interface VideoDropZoneProps {
  value: File | string;
  label: string;
  onChange: (file: File | null, lang?: string) => void;
  onDelete?: () => void;
  onSubmit?: () => void;
  warn?: boolean;
  i18n?: boolean;
  isDetail?: boolean;
  disabled?: boolean;
  playerId?: string;
  onLoad?: (e: any) => void;
  accept?: { [key: string]: string[] };
  /**
   * A picked file is not always a video - a subtitle/audio track shares this
   * dropzone's file-picking chrome (drag/drop, name, upload/delete buttons)
   * but has nothing a `<video>` tag can play. When set, a plain file-name
   * card replaces the video/HLS preview; every other caller is unaffected.
   */
  filePreview?: boolean;
  /**
   * Accept a whole batch in one drop - a season's episode files. `onChange`
   * then fires once per file, in drop order, which is the order the caller
   * has to keep (see `numberFiles`). Off everywhere else: one asset, one file.
   */
  multiple?: boolean;
}

const VideoDropZone = memo(function ({
  value,
  label,
  onChange,
  onDelete,
  onSubmit,
  warn,
  i18n,
  isDetail,
  disabled,
  playerId,
  onLoad,
  accept,
  filePreview,
  multiple,
}: VideoDropZoneProps) {
  const [selectedLang, setSelectedLang] = useState(defaultLanguage.current);
  const dropzoneRef = useRef<DropzoneRef>();

  const openDialog = () => {
    // Note that the ref is set async,
    // so it might be null at some point
    if (dropzoneRef.current) {
      dropzoneRef.current.open();
    }
  };
  if (i18n) {
    value = value[selectedLang];
  }

  return (
    <Box>
      <Dropzone
        accept={accept ? accept : { "video/*": [] }}
        multiple={Boolean(multiple)}
        ref={dropzoneRef}
        noClick={!isDetail && value ? true : false}
        onDrop={(acceptedFiles, fileRejections) => {
          console.log("rejections", fileRejections);

          if (fileRejections.length > 0) {
            toastMessage("Invalid file type", "error");
            return;
          }
          if (acceptedFiles.length === 0) return;

          // Drop order is the caller's fallback numbering, so the batch is
          // handed over in it rather than in whatever order it is processed.
          const files = multiple ? acceptedFiles : [acceptedFiles[0]];

          files.forEach((file) => (i18n ? onChange(file, selectedLang) : onChange(file)));
        }}
      >
        {({ getRootProps, getInputProps }) => (
          <section
            style={{
              border: "1px solid",
              borderColor: warn ? "#f44336" : "#c4c4c4",
              padding: "10px",
              borderRadius: "10px",
              borderStyle: "dashed",
              minHeight: "150px",
            }}
          >
            {i18n && (
              <FormControl
                sx={{ m: 1, minWidth: 50, ml: 0 }}
                size="small"
                variant="standard"
              >
                <Select
                  labelId="select-lang-label"
                  id="select-lang"
                  value={selectedLang}
                  onChange={(e) => setSelectedLang(e.target.value)}
                >
                  {languages.current.map((language) => (
                    <MenuItem
                      key={"video-zone-language-opt-" + language.id}
                      value={language.iso_code}
                    >
                      {language.short_Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <div
              {...getRootProps()}
              style={{ minHeight: "150px" }}
            >
              <input {...getInputProps()} />
              <Typography variant={"subtitle2"}>{label}</Typography>
              {!value && (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    color: "gray",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "130px",
                    cursor: "pointer",
                  }}
                >
                  <UploadFile style={{ width: "40px", height: "40px" }} />
                  <Typography>Upload file</Typography>
                </div>
              )}
              {value && value !== "fake" ? (
                <Box display={"grid"}>
                  {filePreview ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        background: "#ccc",
                        padding: "10px",
                        borderRadius: "4px",
                      }}
                    >
                      <InsertDriveFile />
                      <Typography fontSize={13} sx={{ wordBreak: "break-all" }}>
                        {/* @ts-ignore */}
                        {typeof value === "string" ? value : value.name}
                      </Typography>
                    </Box>
                  ) : typeof value === "string" && value.includes("m3u8") ? (
                    <ReactHlsPlayer
                      playerRef={createRef<HTMLVideoElement>()}
                      src={value}
                      autoPlay={false}
                      controls={true}
                      width="100%"
                      height="auto"
                      onLoad={(e) => {
                        if (onLoad) onLoad(e);
                      }}
                    />
                  ) : (
                    <video
                      id={playerId}
                      // @ts-ignore
                      src={typeof value === "string" ? value : URL.createObjectURL(value)}
                      width={"100%"}
                      onLoadedData={(e) => {
                        if (onLoad) onLoad(e);
                      }}
                      controls
                      style={{ objectFit: "contain", background: "#ccc", padding: "10px" }}
                    />
                  )}

                  {/* @ts-ignore */}
                  {value.name && (
                    <Typography
                      style={{ maxWidth: "50%", wordWrap: "break-word" }}
                      variant={"subtitle2"}
                    >
                      {/* @ts-ignore */}
                      {value.name}
                    </Typography>
                  )}

                  <Stack
                    direction="row"
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    spacing={2}
                    mt={1}
                    width={"100%"}
                  >
                    {!disabled && (
                      <ButtonGroup
                        style={{ width: "100%" }}
                        disableElevation
                        variant="contained"
                        aria-label="Disabled elevation buttons"
                      >
                        {typeof value === "string" ? (
                          <>
                            {onDelete && (
                              <Button
                                color="error"
                                style={{ width: "100%" }}
                                onClick={onDelete}
                                disabled={disabled ? true : false}
                              >
                                DELETE
                              </Button>
                            )}
                          </>
                        ) : (
                          <>
                            {onSubmit && (
                              <Button
                                color="info"
                                style={{ width: "100%" }}
                                disabled={disabled ? true : false}
                                onClick={onSubmit}
                              >
                                UPLOAD
                              </Button>
                            )}
                            <Button
                              color="error"
                              style={{ width: "100%" }}
                              onClick={() => {
                                if (i18n) {
                                  onChange(null, selectedLang);
                                } else {
                                  onChange(null);
                                }
                              }}
                              disabled={disabled ? true : false}
                            >
                              DELETE
                            </Button>
                          </>
                        )}
                      </ButtonGroup>
                    )}
                  </Stack>
                </Box>
              ) : (
                value === "fake" && (
                  <Box display={"grid"}>
                    <Box sx={{ width: 250, height: 250, background: "gray" }} />
                    <ButtonGroup
                      disableElevation
                      variant="contained"
                      aria-label="Disabled elevation buttons"
                    >
                      {isDetail ? (
                        <Button
                          color="info"
                          onClickCapture={(e) => {
                            e.stopPropagation();
                            openDialog();
                          }}
                        >
                          GÜNCELLE
                        </Button>
                      ) : (
                        <Button
                          color="error"
                          onClickCapture={(e) => {
                            e.stopPropagation();
                            if (i18n) {
                              onChange(null, selectedLang);
                            } else {
                              onChange(null);
                            }
                          }}
                        >
                          Sİl
                        </Button>
                      )}
                    </ButtonGroup>
                  </Box>
                )
              )}
            </div>
          </section>
        )}
      </Dropzone>
      {warn && (
        <Typography
          variant="caption"
          color="error"
        >
          This field is mandatory
        </Typography>
      )}
    </Box>
  );
},
isPropsEqual);

function isPropsEqual(prevProps, nextProps) {
  return (
    prevProps.value?.name === nextProps.value?.name &&
    typeof nextProps.value === typeof prevProps.value
  );
}

export default VideoDropZone;
